<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!rows.length" class="rm-empty">
      <template v-if="search">{{ $t("report_mobile.no_results_for", { search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-food-off</v-icon>
        {{ $t("no_taken_company_meals_data") }}
        <span class="rm-muted">{{ $t("select_date_and_filters") }}</span>
      </template>
    </div>

    <template v-else>
      <div v-if="canCheckReport" class="rm-summary">
        <div class="rm-summary__row">
          <div class="rm-summary__labels">
            <span>{{ $t("company_report.taken_company") }}</span>
            <span>
              {{ pluralT("report_mobile.companies_n", rows.length) }} ·
              {{ pluralT("report_mobile.meals_n", summary.ordered) }}
            </span>
          </div>
          <span class="rm-summary__value">
            {{ summary.taken }}<span class="rm-summary__value-of"> / {{ summary.ordered }}</span>
          </span>
        </div>
        <div
          class="rm-progress rm-progress--inverse"
          role="progressbar"
          :aria-valuenow="summary.taken"
          aria-valuemin="0"
          :aria-valuemax="summary.ordered"
        >
          <div class="rm-progress__fill" :style="{ width: `${summary.percent}%` }"></div>
        </div>
      </div>
      <div v-else class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("report_desktop.total_meals") }}</span>
          <span class="rm-stat__value rm-num">{{ summary.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("companies") }}</span>
          <span class="rm-stat__value rm-num">{{ rows.length }}</span>
        </div>
      </div>

      <ul class="rm-list rm-card">
        <li v-for="row in rows" :key="row.key" class="rm-mrow">
          <div class="rm-mrow__main">
            <span class="rm-mrow__name">{{ row.name }}</span>
            <span class="rm-mrow__value rm-num" :class="{ 'is-complete': canCheckReport && row.complete }">
              <v-icon v-if="canCheckReport && row.complete" size="16" color="#15803d">mdi-check-circle</v-icon>
              <template v-if="canCheckReport">
                {{ row.taken }}<span class="rm-mrow__of"> / {{ row.ordered }}</span>
              </template>
              <template v-else>{{ row.ordered }}</template>
            </span>
          </div>
          <div
            v-if="canCheckReport"
            class="rm-progress cm-tc__progress"
            :class="{ 'rm-progress--complete': row.complete }"
            role="progressbar"
            :aria-valuenow="row.taken"
            aria-valuemin="0"
            :aria-valuemax="row.ordered"
          >
            <div class="rm-progress__fill" :style="{ width: `${row.percent}%` }"></div>
          </div>
          <div v-if="row.shifts.length" class="rm-mrow__shifts">
            <span v-for="shift in row.shifts" :key="shift.id" class="rm-mrow__shift rm-num">
              <report-shift-tag :shift="shift.id"></report-shift-tag>
              <template v-if="canCheckReport">{{ shift.taken }} / {{ shift.orders }}</template>
              <template v-else>{{ shift.orders }}</template>
            </span>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import CompanyReportTabTakenCompany from "@/components/company/reports/companyTabs/CompanyReportTabTakenCompany.vue";
import { SHIFTS } from "@/constants/shifts";
import hasBarShifts from "@/components/report/shared/hasBarShifts";
import hasPlural from "@/components/report/shared/hasPlural";
import hasShiftSubtitle from "@/components/report/shared/hasShiftSubtitle";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { percentOf } from "@/components/report/shared/takenMath";
import "@/components/report/mobile/screens/reportMobileMealCards.scss";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";

/**
 * Mobilni "Preuzeto po firmama": red po firmi sa brojem preuzetih / poručenih obroka i po smeni. Smene, firme i
 * period su u zaglavlju. Nasleđuje učitavanje, štampu i Excel iz starog taba. Mesečni izveštaj firme (dugme u
 * starom tabu) se ovde ne nudi: stari dijalog traži restoran iz store-a, kojeg nalog firme nema.
 */
export default {
  name: "CompanyMobileTakenCompany",
  extends: CompanyReportTabTakenCompany,
  mixins: [hasPlural, hasBarShifts, hasShiftSubtitle, hasPeriodRefetch("fetchMeals")],
  components: { ReportShiftTag },
  computed: {
    rows() {
      const found = this.search
        ? this.customSearch(this.filteredCompanies, [{ value: "name" }], this.search)
        : this.filteredCompanies;
      return found
        .filter((company) => company.total > 0)
        .map((company, index) => {
          const ordered = company.total;
          const taken = company.total_payed || 0;
          return {
            key: `${company.name}-${index}`,
            name: this.toLatin(company.name),
            ordered,
            taken,
            complete: ordered > 0 && taken >= ordered,
            percent: percentOf(taken, ordered),
            shifts: SHIFTS.map((shift) => ({
              id: shift.id,
              orders: company[`shift${shift.id}`]?.quantity || 0,
              taken: company[`shift${shift.id}`]?.quantity_payed || 0,
            })).filter((shift) => shift.orders > 0),
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    summary() {
      const taken = this.rows.reduce((sum, row) => sum + row.taken, 0);
      const ordered = this.rows.reduce((sum, row) => sum + row.ordered, 0);
      return { taken, ordered, percent: percentOf(taken, ordered) };
    },
  },
};
</script>

<style lang="scss">
.rm-theme .cm-tc__progress {
  margin-top: 8px;
}
</style>
